import { clashKey, type ClashResult } from '@viewer/clash/lib'
import { deduzirEspecialidade, REDES, type Especialidade, type MapaDeEspecialidades } from './clashEspecialidades'
import type { ClashTriageEntry, ClashTriageMap } from './clashTriage'

// ---------------------------------------------------------------------------
// Regras de exclusao: o segundo passo do funil (docs/CLASH.md, seccao 11).
//
// Uma regra diz que conflitos nao sao trabalho de reuniao (uma entrega de
// viga numa parede, uma duplicacao de modelacao, um tubo embebido) e
// porque. Os conflitos apanhados ficam "ignorados por regra": saem da
// lista de trabalho e da exportacao BCF por omissao, contam no contador
// "Ignorados por regra (N)" e podem ser reabertos um a um, ou a regra
// inteira (desligando-a).
//
// NAO SE ESCREVE NADA NOS CONFLITOS. O estado "ignorado por regra" e
// calculado: as regras ficam na classificacao (logo na sessao e na
// partilha, um so item de configuracao) e cada conflito e avaliado contra
// elas sempre que a lista se desenha. Daqui sai, de graca, o que a
// reconciliacao pede: um ignorado que volta numa corrida continua
// ignorado (a regra continua a apanha-lo) e, desligada a regra, voltam
// todos. A unica coisa gravada no conflito e a excepcao "reaberto a mao"
// (`foraDaExclusao`, os ids das regras que deixaram de o apanhar), porque
// essa e uma decisao de alguem.
//
// O QUE UMA REGRA APANHA. So conflitos por decidir (estado novo ou
// reaberto): um "aceite" ou um "resolvido" e uma decisao explicita e fica
// a vista com o seu estado.
//
// A condicao e um E de todos os campos preenchidos. Os lados A e B nao
// tem ordem: a regra apanha o par em qualquer das duas orientacoes. Uma
// condicao vazia nao apanha nada (uma regra sem condicao escondia tudo).
//
// Puro: quem chama da os resultados, a triagem e a especialidade de cada
// modelo.
// ---------------------------------------------------------------------------

export interface CondicaoDeExclusao {
  /** Codigos da classificacao automatica (B1, D1, E1, ...). */
  regras?: string[]
  /** Tipo de problema da classificacao (atravessamento, embebido, ...). */
  tipos?: string[]
  kind?: 'hard' | 'clearance'
  /** Especialidades de um lado e do outro; "MEP" vale MAC, HID e EEL. */
  especialidadesA?: string[]
  especialidadesB?: string[]
  /** Os dois elementos da mesma especialidade. */
  mesmaEspecialidade?: boolean
  /** Classes IFC (em maiusculas) de um lado e do outro. */
  classesA?: string[]
  classesB?: string[]
  /** Interferencias com profundidade ate isto (aproximada), mm. */
  profundidadeMaxMm?: number
  /** Folgas com distancia ate isto, mm. */
  folgaMaxMm?: number
  /** Niveis por cota. */
  niveis?: string[]
}

export interface RegraDeExclusao {
  id: string
  nome: string
  /** Porque e que estes conflitos nao sao trabalho de reuniao. */
  motivo: string
  ativa: boolean
  /** Regra sugerida pela plataforma: liga-se e desliga-se, nao se apaga. */
  sugerida?: boolean
  /** Corrige-se no modelo de origem: "Criar topico para o modelador". */
  naOrigem?: boolean
  condicao: CondicaoDeExclusao
}

/** Classes de conduta e tubagem (o isolamento e da conduta ou do tubo). */
const CLASSES_DE_CONDUTA = [
  'IFCDUCTSEGMENT',
  'IFCDUCTFITTING',
  'IFCPIPESEGMENT',
  'IFCPIPEFITTING',
  'IFCFLOWSEGMENT',
  'IFCFLOWFITTING',
]

/** As regras sugeridas, desligadas ate o utilizador as aceitar. */
export function regrasSugeridas(): RegraDeExclusao[] {
  return [
    {
      id: 'sug-entrega',
      nome: 'Entrega de viga/laje EST em parede ARQ',
      motivo:
        'Apoio estrutural intencional: a EST confirma o comprimento de entrega e o ARQ recorta o nicho se for preciso. Não é conflito para reunião.',
      ativa: false,
      sugerida: true,
      condicao: { regras: ['E1'] },
    },
    {
      id: 'sug-duplicacao',
      nome: 'Duplicação de modelação EST/ARQ',
      motivo:
        'O ARQ modela o volume da estrutura. Corrige-se na origem (convenção de modelação), com uma nota para o modelador, e não conflito a conflito.',
      ativa: false,
      sugerida: true,
      naOrigem: true,
      condicao: { regras: ['D1', 'D2'] },
    },
    {
      id: 'sug-embebido',
      nome: 'Elemento embebido',
      motivo:
        'Quadros, caixas e tubos eléctricos embebidos em paredes ou pavimentos ARQ (nicho, roço ou enchimento): prática corrente, aceita-se em bloco.',
      ativa: false,
      sugerida: true,
      condicao: { regras: ['B2', 'B4'] },
    },
    {
      id: 'sug-isolamento',
      nome: 'Isolamento contra a própria conduta',
      motivo:
        'O isolamento (IfcCovering) envolve a conduta ou o tubo da mesma especialidade: a sobreposição é a própria modelação. Só aparece com o teste dentro do mesmo modelo ligado.',
      ativa: false,
      sugerida: true,
      condicao: {
        kind: 'hard',
        mesmaEspecialidade: true,
        classesA: ['IFCCOVERING'],
        classesB: [...CLASSES_DE_CONDUTA],
      },
    },
    {
      id: 'sug-mobiliario-folga',
      nome: 'Mobiliário ARQ na folga',
      motivo:
        'A folga regulamentar é entre redes e estrutura; mobiliário do ARQ perto de uma rede não é falta de espaço de montagem.',
      ativa: false,
      sugerida: true,
      condicao: {
        kind: 'clearance',
        especialidadesA: ['ARQ'],
        classesA: ['IFCFURNITURE', 'IFCFURNISHINGELEMENT', 'IFCSYSTEMFURNITUREELEMENT'],
        especialidadesB: ['MEP'],
      },
    },
  ]
}

const IDS_SUGERIDOS = new Set(regrasSugeridas().map((r) => r.id))

// --- Leitura -----------------------------------------------------------------

function textos(v: unknown, max = 40, maiusculas = false): string[] | undefined {
  if (!Array.isArray(v)) return undefined
  const out: string[] = []
  for (const x of v) {
    if (typeof x !== 'string') continue
    let t = x.trim().slice(0, 80)
    if (maiusculas) t = t.toUpperCase()
    if (t && !out.includes(t)) out.push(t)
    if (out.length >= max) break
  }
  return out.length > 0 ? out : undefined
}

function numero(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : undefined
}

/** Le uma condicao gravada; campo invalido fica de fora. */
export function lerCondicao(v: unknown): CondicaoDeExclusao {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>
  const c: CondicaoDeExclusao = {}
  const regras = textos(o.regras, 40, true)
  if (regras) c.regras = regras
  const tipos = textos(o.tipos)
  if (tipos) c.tipos = tipos
  if (o.kind === 'hard' || o.kind === 'clearance') c.kind = o.kind
  const ea = textos(o.especialidadesA, 10, true)
  if (ea) c.especialidadesA = ea
  const eb = textos(o.especialidadesB, 10, true)
  if (eb) c.especialidadesB = eb
  if (o.mesmaEspecialidade === true) c.mesmaEspecialidade = true
  const ca = textos(o.classesA, 40, true)
  if (ca) c.classesA = ca
  const cb = textos(o.classesB, 40, true)
  if (cb) c.classesB = cb
  const p = numero(o.profundidadeMaxMm)
  if (p !== undefined) c.profundidadeMaxMm = p
  const f = numero(o.folgaMaxMm)
  if (f !== undefined) c.folgaMaxMm = f
  const n = textos(o.niveis)
  if (n) c.niveis = n
  return c
}

/** Le as regras gravadas. Sem nada gravado, as sugeridas (desligadas).
 *  As sugeridas que faltem (gravadas antes de existirem) juntam-se no
 *  fim, desligadas; uma sugerida gravada fica com o estado gravado. */
export function lerExclusoes(v: unknown): RegraDeExclusao[] {
  const out: RegraDeExclusao[] = []
  const vistos = new Set<string>()
  if (Array.isArray(v)) {
    for (const x of v) {
      if (!x || typeof x !== 'object') continue
      const o = x as Record<string, unknown>
      const id = typeof o.id === 'string' ? o.id.trim().slice(0, 60) : ''
      const nome = typeof o.nome === 'string' ? o.nome.trim().slice(0, 120) : ''
      if (!id || !nome || vistos.has(id)) continue
      vistos.add(id)
      out.push({
        id,
        nome,
        motivo: typeof o.motivo === 'string' ? o.motivo.trim().slice(0, 1000) : '',
        ativa: o.ativa === true,
        ...(IDS_SUGERIDOS.has(id) ? { sugerida: true } : {}),
        ...(o.naOrigem === true ? { naOrigem: true } : {}),
        condicao: lerCondicao(o.condicao),
      })
      if (out.length >= 50) break
    }
  }
  for (const s of regrasSugeridas()) if (!vistos.has(s.id)) out.push(s)
  return out
}

/** As regras sao as de omissao (as sugeridas, todas desligadas)? */
export function exclusoesPorOmissao(regras: readonly RegraDeExclusao[]): boolean {
  return JSON.stringify(regras) === JSON.stringify(regrasSugeridas())
}

// --- Avaliacao ---------------------------------------------------------------

/** Um elemento de um conflito, para a condicao. */
export interface ElementoDoConflito {
  modelId: string
  especialidade: Especialidade | null
  classe: string | null
}

/** O que a condicao precisa de saber de um conflito. */
export interface DescritorDoConflito {
  key: string
  kind: 'hard' | 'clearance'
  regra: string | null
  tipo: string | null
  a: ElementoDoConflito
  b: ElementoDoConflito
  /** Profundidade aproximada (duro) e distancia (folga), mm. */
  profundidadeMm: number | null
  folgaMm: number | null
  nivel: string | null
}

export type EspecialidadeDe = (modelId: string) => Especialidade | null

/** A especialidade de um modelo: a corrigida pelo utilizador, a deduzida
 *  dos modelos abertos, ou a deduzida do proprio id (o id e o nome do
 *  ficheiro, por isso serve tambem para modelos que nao estao abertos
 *  neste browser). */
export function especialidadeDosModelos(
  modelos: readonly { id: string; fileName: string }[],
  corrigido: MapaDeEspecialidades = {},
): EspecialidadeDe {
  const porId = new Map<string, Especialidade | null>()
  for (const m of modelos) porId.set(m.id, m.id in corrigido ? corrigido[m.id] : deduzirEspecialidade(m.fileName))
  return (id) => {
    if (porId.has(id)) return porId.get(id) ?? null
    if (id in corrigido) return corrigido[id]
    return deduzirEspecialidade(id)
  }
}

function modelosDaChave(key: string): [string, string] | null {
  const p = key.split('|')
  if (p.length !== 3) return null
  return [p[0].slice(0, p[0].lastIndexOf(':')), p[1].slice(0, p[1].lastIndexOf(':'))]
}

/** O descritor de um conflito: do resultado da corrida, se houver, ou
 *  da ultima geometria conhecida da triagem. As classes so se usam da
 *  mesma fonte que os modelos (a orientacao do par pode ser outra). */
export function descritorDoConflito(
  key: string,
  r: ClashResult | undefined,
  e: ClashTriageEntry | undefined,
  especialidadeDe: EspecialidadeDe,
): DescritorDoConflito | null {
  const kind = key.endsWith('|clearance') ? 'clearance' : 'hard'
  let a: ElementoDoConflito
  let b: ElementoDoConflito
  let depth: number | undefined
  let distance: number | undefined
  const fonte = r ?? e?.ultimo
  if (fonte) {
    a = { modelId: fonte.pair.modelA, especialidade: especialidadeDe(fonte.pair.modelA), classe: fonte.categoryA ?? null }
    b = { modelId: fonte.pair.modelB, especialidade: especialidadeDe(fonte.pair.modelB), classe: fonte.categoryB ?? null }
    depth = fonte.depth
    distance = fonte.distance
  } else {
    const ms = modelosDaChave(key)
    if (!ms) return null
    a = { modelId: ms[0], especialidade: especialidadeDe(ms[0]), classe: null }
    b = { modelId: ms[1], especialidade: especialidadeDe(ms[1]), classe: null }
  }
  return {
    key,
    kind,
    regra: e?.regra ?? null,
    tipo: e?.tipoConflito ?? null,
    a: { ...a, classe: a.classe ? a.classe.toUpperCase() : null },
    b: { ...b, classe: b.classe ? b.classe.toUpperCase() : null },
    profundidadeMm: typeof depth === 'number' ? depth * 1000 : null,
    folgaMm: typeof distance === 'number' ? distance * 1000 : null,
    nivel: e?.nivel ?? null,
  }
}

/** A condicao nao tem nenhum campo preenchido. */
export function condicaoVazia(c: CondicaoDeExclusao): boolean {
  return (
    !c.regras?.length &&
    !c.tipos?.length &&
    !c.kind &&
    !c.especialidadesA?.length &&
    !c.especialidadesB?.length &&
    !c.mesmaEspecialidade &&
    !c.classesA?.length &&
    !c.classesB?.length &&
    c.profundidadeMaxMm === undefined &&
    c.folgaMaxMm === undefined &&
    !c.niveis?.length
  )
}

function especialidadeServe(lista: readonly string[] | undefined, e: Especialidade | null): boolean {
  if (!lista || lista.length === 0) return true
  if (!e) return false
  return lista.some((x) => x === e || (x === 'MEP' && REDES.has(e)))
}

function ladoServe(esps: readonly string[] | undefined, classes: readonly string[] | undefined, el: ElementoDoConflito): boolean {
  if (!especialidadeServe(esps, el.especialidade)) return false
  if (classes && classes.length > 0) return !!el.classe && classes.includes(el.classe)
  return true
}

function semAcentos(t: string): string {
  return t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()
}

/** O conflito cumpre a condicao? */
export function cumpreCondicao(c: CondicaoDeExclusao, d: DescritorDoConflito): boolean {
  if (condicaoVazia(c)) return false
  if (c.kind && c.kind !== d.kind) return false
  if (c.regras?.length && !(d.regra && c.regras.includes(d.regra.toUpperCase()))) return false
  if (c.tipos?.length && !(d.tipo && c.tipos.some((t) => semAcentos(t) === semAcentos(d.tipo!)))) return false
  if (c.niveis?.length && !(d.nivel && c.niveis.includes(d.nivel))) return false
  if (c.mesmaEspecialidade && !(d.a.especialidade && d.a.especialidade === d.b.especialidade)) return false
  if (c.profundidadeMaxMm !== undefined) {
    if (d.kind !== 'hard' || d.profundidadeMm === null || d.profundidadeMm > c.profundidadeMaxMm) return false
  }
  if (c.folgaMaxMm !== undefined) {
    if (d.kind !== 'clearance' || d.folgaMm === null || d.folgaMm > c.folgaMaxMm) return false
  }
  const directo = ladoServe(c.especialidadesA, c.classesA, d.a) && ladoServe(c.especialidadesB, c.classesB, d.b)
  const trocado = ladoServe(c.especialidadesA, c.classesA, d.b) && ladoServe(c.especialidadesB, c.classesB, d.a)
  return directo || trocado
}

/** A regra pode apanhar esta entrada? So conflitos por decidir. */
export function apanhavel(e: ClashTriageEntry | undefined): boolean {
  return !e || e.state === 'novo' || e.state === 'reaberto'
}

export interface AvaliacaoDasExclusoes {
  /** Conflito ignorado -> id da regra activa que o apanhou (a primeira). */
  ignorados: Map<string, string>
  /** Por regra (activa ou nao): as chaves que apanha agora (sem as
   *  reabertas a mao), e as reabertas a mao. */
  porRegra: Record<string, { apanha: string[]; reabertos: string[] }>
}

/**
 * Avalia as regras sobre os conflitos da corrida. As regras desligadas
 * tambem se avaliam: o painel diz quantos apanhariam antes de as ligar.
 */
export function avaliarExclusoes(
  results: readonly ClashResult[],
  triagem: ClashTriageMap,
  regras: readonly RegraDeExclusao[],
  especialidadeDe: EspecialidadeDe,
): AvaliacaoDasExclusoes {
  const ignorados = new Map<string, string>()
  const porRegra: AvaliacaoDasExclusoes['porRegra'] = {}
  for (const r of regras) porRegra[r.id] = { apanha: [], reabertos: [] }
  const uteis = regras.filter((r) => !condicaoVazia(r.condicao))
  if (uteis.length === 0) return { ignorados, porRegra }
  for (const res of results) {
    const key = clashKey(res)
    const e = triagem[key]
    if (!apanhavel(e)) continue
    const d = descritorDoConflito(key, res, e, especialidadeDe)
    if (!d) continue
    const fora = e?.foraDaExclusao ?? []
    for (const regra of uteis) {
      if (!cumpreCondicao(regra.condicao, d)) continue
      if (fora.includes(regra.id)) {
        porRegra[regra.id].reabertos.push(key)
        continue
      }
      porRegra[regra.id].apanha.push(key)
      if (regra.ativa && !ignorados.has(key)) ignorados.set(key, regra.id)
    }
  }
  return { ignorados, porRegra }
}

/** As regras que apanham um conflito agora (para o reabrir de todas). */
export function regrasQueApanham(
  key: string,
  r: ClashResult | undefined,
  e: ClashTriageEntry | undefined,
  regras: readonly RegraDeExclusao[],
  especialidadeDe: EspecialidadeDe,
): string[] {
  const d = descritorDoConflito(key, r, e, especialidadeDe)
  if (!d) return []
  return regras.filter((x) => x.ativa && cumpreCondicao(x.condicao, d)).map((x) => x.id)
}

/** O patch que reabre um conflito: as regras que o apanham passam a
 *  excepcao dele. */
export function patchDeReabrir(
  e: ClashTriageEntry | undefined,
  ids: readonly string[],
): { foraDaExclusao: string[] } {
  return { foraDaExclusao: [...new Set([...(e?.foraDaExclusao ?? []), ...ids])] }
}

/** O patch que devolve um conflito reaberto a regra. */
export function patchDeVoltarARegra(
  e: ClashTriageEntry | undefined,
  id: string,
): { foraDaExclusao: string[] } {
  return { foraDaExclusao: (e?.foraDaExclusao ?? []).filter((x) => x !== id) }
}

// --- Textos ------------------------------------------------------------------

function lista(xs: readonly string[]): string {
  return xs.join(', ')
}

/** A condicao por extenso ("regras E1; EST × ARQ; duro"). */
export function textoDaCondicao(c: CondicaoDeExclusao): string {
  if (condicaoVazia(c)) return 'sem condição (não apanha nada)'
  const partes: string[] = []
  if (c.regras?.length) partes.push(`regra${c.regras.length === 1 ? '' : 's'} ${lista(c.regras)}`)
  if (c.tipos?.length) partes.push(`tipo ${lista(c.tipos)}`)
  if (c.kind) partes.push(c.kind === 'hard' ? 'interferências duras' : 'folgas')
  const lado = (esps?: string[], classes?: string[]) => {
    const t = [esps?.length ? lista(esps) : '', classes?.length ? lista(classes) : ''].filter(Boolean).join(' ')
    return t || 'qualquer'
  }
  if (c.especialidadesA?.length || c.especialidadesB?.length || c.classesA?.length || c.classesB?.length) {
    partes.push(`${lado(c.especialidadesA, c.classesA)} × ${lado(c.especialidadesB, c.classesB)}`)
  }
  if (c.mesmaEspecialidade) partes.push('mesma especialidade')
  if (c.profundidadeMaxMm !== undefined) partes.push(`profundidade até ${String(c.profundidadeMaxMm).replace('.', ',')} mm`)
  if (c.folgaMaxMm !== undefined) partes.push(`folga até ${String(c.folgaMaxMm).replace('.', ',')} mm`)
  if (c.niveis?.length) partes.push(`nível ${lista(c.niveis)}`)
  return partes.join('; ')
}

/** Id de uma regra nova, a partir do nome (unico entre as existentes). */
export function idDeRegraNova(nome: string, existentes: readonly RegraDeExclusao[]): string {
  const base =
    'R-' +
    (semAcentos(nome)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40) || 'regra')
  const usados = new Set(existentes.map((r) => r.id))
  let id = base
  let n = 2
  while (usados.has(id)) id = `${base}-${n++}`
  return id
}
